import { Injectable, inject } from '@angular/core';
import { provideTransloco, Translation, TranslocoLoader } from '@jsverse/transloco';

import { environment } from '~app-environment';
import { BUILD_TIME } from '~/core/tokens';

function isPlainObject(value: unknown): value is Record<string, unknown> {
	return !!value && typeof value === 'object' && !Array.isArray(value);
}

function deepMerge<T>(base: T, overrides: unknown): T {
	if (!isPlainObject(base) || !isPlainObject(overrides)) {
		return (overrides ?? base) as T;
	}

	const result: Record<string, unknown> = { ...(base as Record<string, unknown>) };

	for (const [key, overrideValue] of Object.entries(overrides)) {
		const baseValue = result[key];
		if (isPlainObject(baseValue) && isPlainObject(overrideValue)) {
			result[key] = deepMerge(baseValue, overrideValue);
			continue;
		}
		result[key] = overrideValue;
	}

	return result as T;
}

@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
	private readonly buildTime = inject(BUILD_TIME);
	private readonly promiseMap: Record<string, Promise<Translation>> = {};
	private readonly valueMap: Record<string, Translation> = {};
	private readonly isDefaultClient = environment.clientId === 'default';
	private i18nOverridePathsPromise: Promise<Set<string>> | null = null;

	/** Paths listed in assets/i18n/override/manifest.json for which delta JSON should be fetched. */
	private getI18nOverridePaths(): Promise<Set<string>> {
		if (this.isDefaultClient) {
			return Promise.resolve(new Set());
		}

		if (!this.i18nOverridePathsPromise) {
			this.i18nOverridePathsPromise = (async () => {
				const manifestUrl = new URL('assets/i18n/override/manifest.json', document.baseURI);
				manifestUrl.searchParams.set('t', String(this.buildTime));

				try {
					const resp = await fetch(manifestUrl.toString());
					if (!resp.ok) {
						return new Set<string>();
					}
					const data = (await resp.json()) as { paths?: unknown };
					const paths = Array.isArray(data.paths) ? data.paths : [];
					return new Set(paths.filter((p): p is string => typeof p === 'string'));
				} catch {
					return new Set<string>();
				}
			})();
		}

		return this.i18nOverridePathsPromise;
	}

	public async getTranslation(path: string): Promise<Translation> {
		const oldPathArr = path.split('/');
		const pathRaw = oldPathArr.slice(0, -1);
		const objectPath = pathRaw.length === 0 ? ['general'] : pathRaw;
		const newPath = [...oldPathArr.slice(-1), ...objectPath].join('/');

		const storedValue = this.valueMap[newPath];
		if (storedValue) {
			return storedValue;
		}

		const storedPromise = this.promiseMap[newPath];
		if (storedPromise) {
			return await storedPromise;
		}

		if (newPath === 'undefined/general') {
			return {};
		}

		const baseUrl = new URL(`assets/i18n/${newPath}.json`, document.baseURI);
		baseUrl.searchParams.set('t', String(this.buildTime));

		const overrideUrl = new URL(`assets/i18n/override/${newPath}.json`, document.baseURI);
		overrideUrl.searchParams.set('t', String(this.buildTime));

		this.promiseMap[newPath] = new Promise<Translation>(async (resolve) => {
			try {
				const [baseResp, overridePaths] = await Promise.all([
					fetch(baseUrl.toString()),
					this.getI18nOverridePaths(),
				]);

				const base = baseResp.ok ? ((await baseResp.json()) as Translation) : {};
				let overrides: Translation = {};
				if (overridePaths.size > 0 && overridePaths.has(newPath)) {
					const overrideResp = await fetch(overrideUrl.toString());
					overrides = overrideResp.ok ? ((await overrideResp.json()) as Translation) : {};
				}

				const merged = deepMerge(base, overrides);
				this.valueMap[newPath] = merged;
				delete this.promiseMap[newPath];
				resolve(merged);
			} catch (error) {
				console.error(`[i18n] (${newPath})`, error);
				delete this.promiseMap[newPath];
				resolve({});
			}
		});

		return await this.promiseMap[newPath];
	}
}

export const provideRootTransloco = () =>
	provideTransloco({
		config: {
			availableLangs: ['en-gb', 'es-es', 'fr-fr', 'universal'],
			defaultLang: 'en-gb',
			fallbackLang: ['en-gb', 'universal'],
			missingHandler: {
				logMissingKey: true,
				useFallbackTranslation: true,
				allowEmpty: true,
			},
			reRenderOnLangChange: true,
			prodMode: environment.production,
		},
		loader: TranslocoHttpLoader,
	});
